/**
 * Per-page event selection helpers.
 *
 * Every page with an event filter (Bookings, Invitations, Invitations
 * History, Host Scans, Item Consumption, Overview, Event / Field / Scanner /
 * Cashier Supervisor dashboards, Cashless Top Ups and Transactions) keeps its
 * OWN selected event, keyed by the page slug (`pageKey`):
 *  - Redux `pageEvents.byPage[pageKey]` holds it for the session, and
 *    `generalFilters.event` mirrors the selection of the page that is
 *    currently open (set by <PageEventScope> before the page renders).
 *  - An explicit choice is persisted to localStorage under
 *    `admin.selectedEvent.<pageKey>` so the page remembers it after a reload.
 *  - Without a (valid) saved choice the page uses `pickDefaultEvent` below.
 * The lean EVENTS LIST itself is shared app-wide (hooks/useEventsLookup.ts).
 */

/** Lean event row used by the event pickers (see `listEventsLookup`). */
export interface EventLookupItem {
  id: string;
  name?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  published?: boolean | null;
  deleted?: string | null;
  createdAt?: string | null;
}

export const PAGE_EVENT_STORAGE_PREFIX = 'admin.selectedEvent.';
/** Old single global key (before per-page selection); removed on startup. */
export const LEGACY_SELECTED_EVENT_STORAGE_KEY = 'admin.selectedEventId';

/** Saved explicit choice of a page, or `null` when it has none. */
export const readPersistedPageEventId = (pageKey: string): string | null => {
  if (!pageKey) return null;
  try {
    return (
      window.localStorage.getItem(PAGE_EVENT_STORAGE_PREFIX + pageKey) || null
    );
  } catch {
    return null;
  }
};

export const persistPageEventId = (pageKey: string, eventId: string) => {
  if (!pageKey) return;
  try {
    if (eventId) {
      window.localStorage.setItem(PAGE_EVENT_STORAGE_PREFIX + pageKey, eventId);
    } else {
      window.localStorage.removeItem(PAGE_EVENT_STORAGE_PREFIX + pageKey);
    }
  } catch {
    // Storage unavailable (private mode / blocked): selection stays in memory.
  }
};

export const removeLegacySelectedEvent = () => {
  try {
    window.localStorage.removeItem(LEGACY_SELECTED_EVENT_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Event a page may default to or restore from storage. */
export const isSelectableEvent = (e?: EventLookupItem | null): boolean =>
  !!e && !!e.id && e.published === true && e.deleted !== '1';

/**
 * Last events lookup, kept in localStorage so that after a reload the default
 * event can be resolved (and the dashboards can start fetching) immediately,
 * while the fresh list is loaded in the background.
 */
const EVENTS_LOOKUP_STORAGE_KEY = 'admin.eventsLookup';
const EVENTS_LOOKUP_STORAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const readCachedEventsLookup = (): EventLookupItem[] => {
  try {
    const raw = window.localStorage.getItem(EVENTS_LOOKUP_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.items) ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > EVENTS_LOOKUP_STORAGE_MAX_AGE_MS
    ) {
      return [];
    }
    return parsed.items.filter((e: any) => e && typeof e.id === 'string');
  } catch {
    return [];
  }
};

export const writeCachedEventsLookup = (items: EventLookupItem[]) => {
  try {
    const lean = items.map((e) => ({
      id: e.id,
      name: e.name ?? null,
      startDate: e.startDate ?? null,
      endDate: e.endDate ?? null,
      published: e.published ?? null,
      deleted: e.deleted ?? null,
      createdAt: e.createdAt ?? null,
    }));
    window.localStorage.setItem(
      EVENTS_LOOKUP_STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), items: lean }),
    );
  } catch {
    // quota / storage unavailable: the lookup just isn't cached
  }
};

const time = (value?: string | null): number => {
  if (!value) return NaN;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? NaN : t;
};

/**
 * Default event rule (the ONLY place it is defined):
 *  1. Candidates: published === true and not deleted (deleted !== '1').
 *  2. Among candidates that have not ended yet (endDate >= now; when an event
 *     has no endDate its startDate is used), pick the one with the LATEST
 *     startDate.
 *  3. If every candidate has already ended, pick the one that ended most
 *     recently (latest endDate, falling back to startDate).
 *  4. No published event at all -> null (pages then show their empty state).
 */
export const pickDefaultEvent = <T extends EventLookupItem>(
  events: T[] | null | undefined,
  now: number = Date.now(),
): T | null => {
  const candidates = (events || []).filter((e) => isSelectableEvent(e));
  if (candidates.length === 0) return null;

  const endOf = (e: T) => {
    const end = time(e.endDate);
    return Number.isNaN(end) ? time(e.startDate) : end;
  };
  const startOf = (e: T) => {
    const start = time(e.startDate);
    return Number.isNaN(start) ? Number.NEGATIVE_INFINITY : start;
  };

  const notEnded = candidates.filter((e) => {
    const end = endOf(e);
    return !Number.isNaN(end) && end >= now;
  });
  if (notEnded.length > 0) {
    return notEnded.reduce((best, e) => (startOf(e) > startOf(best) ? e : best));
  }

  const endedRank = (e: T) => {
    const end = endOf(e);
    return Number.isNaN(end) ? Number.NEGATIVE_INFINITY : end;
  };
  return candidates.reduce((best, e) =>
    endedRank(e) > endedRank(best) ? e : best,
  );
};
